import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { AlertCircle, Lock, CreditCard } from 'lucide-react';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string);

const brandStyles = {
  primary: 'bg-[#2B8B3B] text-white hover:bg-[#236B2F]',
};

export interface SavedCardInfo {
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
}

interface StripeCheckoutProps {
  clientSecret: string;
  returnUrl: string;
  totalCents: number;
  policyAgreed: boolean;
  bookingType: 'auto' | 'request';
  // Saved card props
  savedCard: SavedCardInfo | null;
  usingSavedCard: boolean;
  saveCard: boolean;
  onSaveCardChange: (v: boolean) => void;
  onUseDifferentCard: () => void;
}

export function StripeCheckout(props: StripeCheckoutProps) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: props.clientSecret,
        appearance: {
          theme: 'stripe',
          variables: { colorPrimary: '#2B8B3B' },
        },
      }}
    >
      <CheckoutForm {...props} />
    </Elements>
  );
}

function brandLabel(brand: string) {
  const map: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'Amex',
    discover: 'Discover',
    diners: 'Diners',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return map[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

function CheckoutForm({
  clientSecret,
  returnUrl,
  totalCents,
  policyAgreed,
  bookingType,
  savedCard,
  usingSavedCard,
  saveCard,
  onSaveCardChange,
  onUseDifferentCard,
}: StripeCheckoutProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const totalDisplay = `$${(totalCents / 100).toFixed(2)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !policyAgreed) return;

    setSubmitting(true);
    setErrorMessage(null);

    let error;

    if (usingSavedCard) {
      // Pay using the payment_method already attached to the PaymentIntent server-side
      ({ error } = await stripe.confirmPayment({
        clientSecret,
        confirmParams: { return_url: returnUrl },
      }));
    } else {
      // Standard flow — collect card via PaymentElement
      if (!elements) { setSubmitting(false); return; }
      ({ error } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
      }));
    }

    // confirmPayment only reaches here on error (success redirects away)
    if (error) {
      setErrorMessage(error.message ?? 'Payment failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* STATE B — saved card summary */}
      {usingSavedCard && savedCard ? (
        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                {brandLabel(savedCard.brand)} ···· {savedCard.last4}
              </p>
              <p className="text-xs text-gray-500">
                Expires {savedCard.expMonth}/{String(savedCard.expYear).slice(-2)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onUseDifferentCard}
            className="text-xs text-[#2B8B3B] hover:underline font-medium"
          >
            Use a different card
          </button>
        </div>
      ) : (
        /* STATE A / C — full PaymentElement + save opt-in */
        <>
          <PaymentElement options={{ wallets: { link: 'never' } }} />
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={saveCard}
              onChange={(e) => onSaveCardChange(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 accent-[#2B8B3B]"
            />
            <span className="text-sm text-gray-600">Save this card for future bookings</span>
          </label>
        </>
      )}

      {errorMessage && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {errorMessage}
        </div>
      )}

      <Button
        type="submit"
        className={`w-full ${brandStyles.primary}`}
        disabled={!stripe || (!usingSavedCard && !elements) || submitting || !policyAgreed}
      >
        <Lock className="w-4 h-4 mr-2" />
        {submitting
          ? 'Processing…'
          : bookingType === 'request'
          ? `Request Booking · ${totalDisplay}`
          : `Pay ${totalDisplay}`}
      </Button>

      {!policyAgreed && (
        <p className="text-xs text-center text-gray-500">
          Accept the cancellation policy above to continue.
        </p>
      )}
    </form>
  );
}
